// Zielwahl im Antrag und im Release (Mehrzielmodus, Vertrag V.3/V.4): Liste, Vorbelegung und Nachziehen.
// Reine Funktionen auf der Board-Antwort; der Server prüft jede Wahl erneut.

/** Alle Ziele einer Stufe, nur bei mehr als einem Ziel (sonst `null`: keine Zielwahl). */
export function stageTargetList(board, stage) {
  const row = board?.stages?.find(item => item.key === stage);
  const list = row?.allTargets || row?.targets;
  return board?.multiTarget && Array.isArray(list) && list.length > 1 ? list : null;
}

const packagesOf = (board, packageIds) => (board?.packages || []).filter(pkg => packageIds.includes(pkg.packageId));
const defaultId = list => (list.find(target => target.isDefault) || list[0]).targetId;

// Ziele der Zielstufe, die den Stand der Quellstufe schon haben (gleicher scopeDigest, erreicht oder laufend).
function reachedTargets(board, pkg, sourceStage, targetStage) {
  const source = pkg.cells?.[sourceStage];
  const byTarget = pkg.targets?.[targetStage] || {};
  return Object.entries(byTarget).filter(([, cell]) => source && cell.scopeDigest === source.scopeDigest
    && ["reached", "running"].includes(cell.state)).map(([id]) => id);
}

/**
 * Vorbelegung der Zielwahl mit Grund (Vertrag V.13): `{ ids, reason }`, `reason` ist "wizard" (aus dem Transport),
 * "region" oder "default". Die Auswahl des Wizards (`pkg.stageTargets.byStage[stage]`, nur Webapp) und die Region verlieren
 * Ziele, die den Stand schon haben oder bekommen (erreicht oder laufend), mit Rückfall auf die volle Menge.
 */
export function initialTargets({ board, targetStage, sourceStage, packageIds, region }) {
  const list = stageTargetList(board, targetStage);
  if (!list) return null;
  const ids = list.map(target => target.targetId);
  const pkgs = packagesOf(board, packageIds);
  const open = base => {
    const rest = base.filter(id => pkgs.some(pkg => !reachedTargets(board, pkg, sourceStage, targetStage).includes(id)));
    return rest.length ? rest : base;
  };
  const wizard = new Set(pkgs.flatMap(pkg => pkg.stageTargets?.byStage?.[targetStage] || []).filter(id => ids.includes(id)));
  if (wizard.size) return { ids: open(ids.filter(id => wizard.has(id))), reason: "wizard" };
  const fromRegion = region ? list.filter(target => target.region === region).map(target => target.targetId) : [];
  if (fromRegion.length) return { ids: open(fromRegion), reason: "region" };
  return { ids: open([defaultId(list)]), reason: "default" };
}

/** Nachziehen: Jedes Paket hat den Stand der Quellstufe in der Zielstufe schon in mindestens einem Ziel (gleicher Tree). */
export function isRollout({ board, sourceStage, targetStage, packageIds }) {
  if (!stageTargetList(board, targetStage)) return false;
  const pkgs = packagesOf(board, packageIds);
  return pkgs.length > 0 && pkgs.every(pkg => {
    const tree = pkg.cells?.[sourceStage]?.tree;
    return !!tree && Object.values(pkg.targets?.[targetStage] || {}).some(cell => cell.tree === tree && ["reached", "running"].includes(cell.state));
  });
}

/** `targets` für den Antrag; ohne Zielwahl oder genau das Standardziel nichts (Anfrage bleibt wie bisher). */
export function requestTargets(list, value) {
  if (!list || !value) return undefined;
  const standard = defaultId(list);
  return value.length === 1 && value[0] === standard ? undefined : value;
}
